import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Mail, Phone, User } from "lucide-react";
import { useTranslation } from "react-i18next";

const formSchema = z.object({
  name: z.string().trim().min(2, { message: "İsim en az 2 karakter olmalıdır" }).max(100, { message: "İsim çok uzun" }),
  email: z.string().trim().email({ message: "Geçerli bir email adresi giriniz" }).max(255, { message: "Email çok uzun" }),
  phone: z.string().trim().min(10, { message: "Geçerli bir telefon numarası giriniz" }).max(20, { message: "Telefon numarası çok uzun" }),
  message: z.string().trim().min(10, { message: "Mesaj en az 10 karakter olmalıdır" }).max(1000, { message: "Mesaj çok uzun" }),
});

type FormData = z.infer<typeof formSchema>;

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      message: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      const validatedData = formSchema.parse(data);

      const { supabase } = await import("@/integrations/supabase/client");
      const { saveContactMessage } = await import("@/integrations/firebase/messages");

      // Mesaj panele kaydedilir ve e-posta bildirimi ayrıca gönderilir; biri başarılıysa talep ulaşmış sayılır.
      const [saved, mailed] = await Promise.allSettled([
        saveContactMessage({ name: validatedData.name, email: validatedData.email, phone: validatedData.phone, message: validatedData.message, source: "İletişim formu" }),
        supabase.functions.invoke('send-contact-email', {
          body: {
            name: validatedData.name,
            email: validatedData.email,
            phone: validatedData.phone,
            message: validatedData.message,
          },
        }),
      ]);
      const savedOk = saved.status === "fulfilled" && saved.value;
      const mailedOk = mailed.status === "fulfilled" && !mailed.value.error;

      if (!savedOk && !mailedOk) {
        console.error("Error sending message:", saved, mailed);
        toast({
          title: "Hata",
          description: "Mesaj gönderilemedi. Lütfen daha sonra tekrar deneyin.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Başarılı!",
        description: "Mesajınız başarıyla gönderildi. En kısa sürede dönüş yapacağız.",
      });
      
      form.reset();
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Hata",
        description: "Bir hata oluştu. Lütfen tekrar deneyin.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('contact.form.name')}
              </FormLabel>
              <FormControl>
                <Input placeholder={t('contact.form.namePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {t('contact.form.email')}
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder={t('contact.form.emailPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {t('contact.form.phone')}
              </FormLabel>
              <FormControl>
                <Input placeholder={t('contact.form.phonePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('contact.form.message')}</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder={t('contact.form.messagePlaceholder')} 
                  className="min-h-[120px]"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full">
          {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
        </Button>
      </form>
    </Form>
  );
}
